import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { LandscapeType, PlotReportFactElement, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { GraphFact } from '@/components/Charts/types';
import { NO_RADIUS, useMapRadiusContext } from '@/components/MapOverview/components/MapRadiusContextProvider';
import { NCCardSectionHeader, printLatestDate } from '@/components/NCCard/NCCard';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { isNullish } from '@/utils/booleans';
import { printMonthYear } from '@/utils/formatting/date';

export const LandCoverChangeStatsTile = ({
  landCoverChange,
}: {
  landCoverChange: PlotReportFactElement<GraphFact[]>;
}) => {
  const { t } = useTranslation();
  const { radius } = useMapRadiusContext();

  const noStatsData = isNullish(landCoverChange.value);

  if (noStatsData) {
    return null;
  }

  const statsData = landCoverChange.value.sort((a, b) => a.date.localeCompare(b.date));

  const latestAnalysisDate = printLatestDate([landCoverChange.measured_at]);

  return (
    <Stack className='gap-8 px-6 py-8 lg:gap-4' data-testid='land-cover-change-stats-tile'>
      <div className='flex flex-col gap-8 lg:gap-4'>
        <NCCardSectionHeader
          title={t('shared.ncaDetail.details.landCoverChange.labels.landCoverChangeWithRadius', {
            radius,
          })}
          supplementaryInfo={
            latestAnalysisDate ? `${t('shared.ncaDetail.details.labels.updated')} ${latestAnalysisDate}` : undefined
          }
          popover={{
            title: t('shared.ncaDetail.details.landCoverChange.labels.landCoverChangeWithRadius', {
              radius,
            }),
            body: t('shared.ncaDetail.details.landCoverChange.stats.landCoverChangeTooltip'),
          }}
        />
        <Stack className='mt-4 gap-4 rounded-2xl bg-neutral-black-4 px-4 py-6'>
          <span className='typography-body1Bold'>
            {t('shared.ncaDetail.details.landCoverChange.stats.explainer.title')}
          </span>
          <LandscapeChangeExplainer statsData={statsData} radius={radius} />
        </Stack>
      </div>
    </Stack>
  );
};

const LandscapeChangeExplainer = ({ statsData, radius }: { statsData: GraphFact[] | undefined; radius: string }) => {
  const { t } = useTranslation();

  if (!statsData || !statsData.length) {
    return (
      <p className='typography-body1 text-text-secondary'>
        {t('shared.ncaDetail.details.landCoverChange.stats.explainer.noData')}
      </p>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const startDate = statsData.at(0)?.date ? printMonthYear(statsData.at(0)!.date) : '';
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const endDate = statsData.at(-1)?.date ? printMonthYear(statsData.at(-1)!.date) : '';

  const landscapeTypesDataText = Object.values(LandscapeType).flatMap((landscapeType) => {
    const landscapeTypeData = statsData.filter((entry) =>
      entry?.labels?.some(
        (label) => label.name === PlotReportFactLabelNameEnum.landscape_type && label.value === landscapeType,
      ),
    );

    if (landscapeTypeData.length <= 1) {
      return [];
    }

    const sortedLandscapeTypeData = landscapeTypeData.sort((a, b) => a.date.localeCompare(b.date));

    const startDataEntry = sortedLandscapeTypeData?.at(0);
    const endDataEntry = sortedLandscapeTypeData?.at(-1);

    const startDateTotalValue = statsData.reduce((acc, curr) => {
      return curr.date === startDataEntry?.date && typeof curr.value === 'number' ? acc + curr.value : acc;
    }, 0);
    const endDateTotalValue = statsData.reduce((acc, curr) => {
      return curr.date === endDataEntry?.date && typeof curr.value === 'number' ? acc + curr.value : acc;
    }, 0);

    if (
      !startDataEntry?.value ||
      !endDataEntry?.value ||
      !startDateTotalValue ||
      !endDateTotalValue ||
      startDataEntry.date === endDataEntry.date
    ) {
      return [];
    }

    const startValue = (startDataEntry.value / startDateTotalValue) * 100;
    const endValue = (endDataEntry.value / endDateTotalValue) * 100;

    if (startValue === null || endValue === null) {
      return [];
    }

    const changePct = endValue - startValue;
    const landscapeTypeLabel = t(`global.landscapeTypes.${landscapeType}`);

    const changePctLabel = getDisplayNumber(changePct, window.navigator.language, { signDisplay: 'exceptZero' });
    let changeVariationText = t('shared.ncaDetail.details.landCoverChange.stats.explainer.noChange', {
      landscapeType: landscapeTypeLabel,
    });

    if (changePct !== 0) {
      changeVariationText = t('shared.ncaDetail.details.landCoverChange.stats.explainer.change', {
        landscapeType: landscapeTypeLabel,
        changePct: changePctLabel,
      });
    }

    return changeVariationText;
  });

  return (
    <p className='typography-body1 text-text-secondary'>
      {t(
        radius === NO_RADIUS
          ? 'shared.ncaDetail.details.landCoverChange.stats.explainer.bodyNoRadius'
          : 'shared.ncaDetail.details.landCoverChange.stats.explainer.body',
        {
          startDate,
          endDate,
          radius,
        },
      )}
      {landscapeTypesDataText
        .map((text, index) => {
          const andWord = t('shared.ncaDetail.details.landCoverChange.stats.explainer.and');

          if (index === landscapeTypesDataText.length - 1) {
            return `${andWord} ${text}.`;
          }
          return text;
        })
        .join(', ')}
    </p>
  );
};
